import { useState, useEffect, useCallback, useRef } from 'react';
import { Eye, ScanLine, Trash2 } from 'lucide-react';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { useToast } from '@/context/ToastContext';
import { rpc } from '@/api/client';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Pagination } from '@/components/common/Pagination';
import { ToolHeader } from '@/components/common/ToolHeader';
import { FilterBar } from '@shared/search';
import { tyreSchema } from './tyreSchema';
import { BarcodeScanner } from './BarcodeScanner';
import type { ListTyreOverviewResponse, GetTyreOverviewPositionResponse, TyreOverviewEntry } from '@/types/telemetry';

const PAGE_SIZE = 20;
const DEFAULT_QUERY = 'discarded:false';

function getTyreColor(laps: number): string {
  if (laps === 0) return 'hsl(140, 60%, 30%)';
  const ratio = Math.min(laps / 70, 1);
  const hue = 120 * (1 - ratio);
  return `hsl(${hue}, 70%, 50%)`;
}

export function TyreOverview() {
  const { params, navigate } = useRouter();
  const { t } = useI18n();
  const { showToast } = useToast();
  
  const page = parseInt(params.page || '1', 10);
  const query = params.q !== undefined ? params.q : DEFAULT_QUERY;

  const [data, setData] = useState<ListTyreOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [discardingId, setDiscardingId] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const loadData = useCallback(async (currentPage: number, currentQuery: string) => {
    try {
      setLoading(true);
      const rpcBody: Record<string, unknown> = { page: currentPage, pageSize: PAGE_SIZE };
      if (currentQuery) rpcBody.query = currentQuery;

      const res = await rpc<ListTyreOverviewResponse>('ListTyreOverview', rpcBody);
      setData(res);
    } catch (err) {
      showToast(t('common.failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t, showToast]);

  useEffect(() => {
    loadData(page, query);
  }, [page, query, loadData]);


  // Scroll to highlighted tyre once data is loaded and rendered
  useEffect(() => {
    if (highlightedId && !loading && (data?.tyres?.length ?? 0) > 0) {
      const timer = setTimeout(() => {
        const el = document.getElementById(`tyre-${highlightedId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [highlightedId, loading, data]);

  const handleSearch = useCallback((newQuery: string) => {
    const qp = new URLSearchParams();
    qp.set('page', '1');
    qp.set('q', newQuery);
    navigate(`#/analysis/tyre-overview?${qp.toString()}`);
  }, [navigate]);

  const handlePageChange = useCallback((newPage: number) => {
    const qp = new URLSearchParams();
    qp.set('page', String(newPage));
    if (params.q !== undefined) qp.set('q', params.q);
    navigate(`#/analysis/tyre-overview?${qp.toString()}`);
  }, [params.q, navigate]);

  const handleScanSuccess = useCallback(async (barcode: string) => {
    setScanning(false);
    try {
      const res = await rpc<GetTyreOverviewPositionResponse>('GetTyreOverviewPosition', {
        barcode,
        pageSize: PAGE_SIZE,
      });
      if (res && res.tyreId && res.page > 0) {
        setHighlightedId(res.tyreId);
        const targetPage = res.page;
        navigate(targetPage > 1 ? `#/analysis/tyre-overview?page=${targetPage}` : '#/analysis/tyre-overview');
        
        // If we are already on that page with default query, manually scroll to target
        if (page === targetPage && query === DEFAULT_QUERY) {
          setTimeout(() => {
            const el = document.getElementById(`tyre-${res.tyreId}`);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }
      } else {
        showToast(t('tyreOverview.scanNotFound'), 'error');
      }
    } catch (err) {
      showToast(t('tyreOverview.scanNotFound'), 'error');
    }
  }, [page, query, navigate, showToast, t]);

  const handleDiscard = useCallback(async (tyre: TyreOverviewEntry) => {
    try {
      setDiscardingId(tyre.id);
      await rpc('UpdateTyre', {
        id: tyre.id,
        tyreProfileId: tyre.tyreProfileId,
        nr: tyre.nr,
        discarded: true,
      });
      showToast(t('tyreOverview.discardSuccess'), 'success');
      loadData(page, query);
    } catch (err) {
      showToast(t('common.failedToSave'), 'error');
    } finally {
      setDiscardingId(null);
    }
  }, [page, query, loadData, showToast, t]);

  const total = data?.totalCount ?? 0;
  const fresh = data?.totalFresh ?? 0;
  const worn = data?.totalWorn ?? 0;
  const eol = data?.totalEndOfLife ?? 0;
  const tyres = data?.tyres ?? [];
  const totalPages = data?.totalCount ? Math.ceil(data.totalCount / PAGE_SIZE) : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col gap-6" ref={containerRef}>
      <ToolHeader title={t('analysis.tyreOverview.title')} icon={Eye} className="mb-2" />

      {/* Search & Scan */}
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <FilterBar
            initialQuery={query}
            schema={tyreSchema}
            onSearch={handleSearch}
            placeholder={t('tyreOverview.searchPlaceholder')}
            enableFilterUI={true}
          />
        </div>
        <button
          onClick={() => setScanning(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent/10 text-accent hover:bg-accent/20 transition-colors font-medium text-sm cursor-pointer whitespace-nowrap flex-shrink-0"
        >
          <ScanLine size={16} />
          <span className="hidden sm:inline">{t('tyreOverview.scanButton')}</span>
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {/* Summary bar */}
          <div className="bg-bg-surface border border-border-subtle rounded-xl p-4 flex flex-wrap gap-4 text-sm font-medium">
            <span className="text-text-primary">{t('tyreOverview.summaryTotal')}: {total}</span>
            <span className="text-text-muted">•</span>
            <span className="text-text-primary">{t('tyreOverview.summaryFresh')}: {fresh}</span>
            <span className="text-text-muted">•</span>
            <span className="text-text-primary">{t('tyreOverview.summaryWorn')}: {worn}</span>
            <span className="text-text-muted">•</span>
            <span className="text-text-primary">{t('tyreOverview.summaryEndOfLife')}: {eol}</span>
          </div>

          {tyres.length === 0 ? (
            <div className="card text-center py-12 text-text-muted">
              {t('tyreOverview.noTyres')}
            </div>
          ) : (
            <>
              <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="divider text-text-muted text-left text-[0.7rem] uppercase tracking-wider font-semibold">
                        <th className="px-4 py-2.5 w-8"></th>
                        <th className="px-4 py-2.5">{t('tyreOverview.colTyre')}</th>
                        <th className="px-4 py-2.5">{t('tyreOverview.colCompound')}</th>
                        <th className="px-4 py-2.5 text-right">{t('tyreOverview.colLaps')}</th>
                        <th className="px-4 py-2.5 hidden sm:table-cell">{t('tyreOverview.colLastUsed')}</th>
                        <th className="px-4 py-2.5">{t('tyreOverview.colStatus')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tyres.map((tyre, index) => {
                        const isEol = tyre.totalLapsDriven >= 70;
                        const isDiscarded = Boolean(tyre.discarded);
                        const isHighlighted = highlightedId === tyre.id;
                        const color = getTyreColor(tyre.totalLapsDriven);

                        return (
                          <tr
                            key={tyre.id}
                            id={`tyre-${tyre.id}`}
                            className={`hover:bg-bg-hover transition-colors ${
                              index < tyres.length - 1 ? 'divider' : ''
                            } ${
                              isHighlighted
                                ? 'bg-purple/10 ring-1 ring-purple/30 font-medium'
                                : isEol || isDiscarded
                                  ? 'opacity-60'
                                  : ''
                            }`}
                          >
                            {/* Color indicator */}
                            <td className="px-4 py-3">
                              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full" style={{ backgroundColor: color }} />
                            </td>

                            {/* Tyre name + nr + (on mobile: last used) */}
                            <td className="px-4 py-3">
                              <div className="font-semibold text-text-primary text-xs sm:text-sm">{tyre.brand} {tyre.type}</div>
                              <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-text-muted mt-0.5">
                                {tyre.nr && <span>{tyre.nr}</span>}
                                {tyre.nr && tyre.lastUsedDate && <span className="sm:hidden text-text-muted/40">•</span>}
                                {tyre.lastUsedDate && (
                                  <span className="sm:hidden text-[11px] text-text-muted mono">
                                    {tyre.lastUsedTrackName ? `${tyre.lastUsedTrackName} · ` : ''}{tyre.lastUsedDate}
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Compound */}
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider
                                ${tyre.compound.toLowerCase() === 'wet' ? 'bg-accent-subtle text-accent-hover border border-accent/20' : 'bg-green/20 text-green'}
                              `}>
                                {tyre.compound.toLowerCase() === 'wet' ? 'WET' : 'DRY'}
                              </span>
                            </td>

                            {/* Lap count */}
                            <td className="px-4 py-3 text-right">
                              <span className="mono font-bold text-text-primary text-xs sm:text-sm">{tyre.totalLapsDriven}</span>
                            </td>

                            {/* Last used (desktop column) */}
                            <td className="px-4 py-3 hidden sm:table-cell text-text-muted text-xs mono">
                              {tyre.lastUsedDate ? (
                                <span>{tyre.lastUsedTrackName ? `${tyre.lastUsedTrackName} · ` : ''}{tyre.lastUsedDate}</span>
                              ) : (
                                <span>—</span>
                              )}
                            </td>

                            {/* Status & Actions */}
                            <td className="px-4 py-3">
                              {isDiscarded ? (
                                <span className="text-xs text-red font-medium">{t('materials.tyre.discarded')}</span>
                              ) : isEol && tyre.canEdit ? (
                                <button
                                  onClick={() => handleDiscard(tyre)}
                                  disabled={discardingId === tyre.id}
                                  title={t('tyreOverview.discardButton')}
                                  aria-label={t('tyreOverview.discardButton')}
                                  className="inline-flex items-center gap-1 p-1 sm:px-2 sm:py-0.5 rounded text-[11px] font-medium bg-red/10 text-red hover:bg-red/20 border border-red/30 transition-colors cursor-pointer disabled:opacity-50"
                                >
                                  <Trash2 size={12} />
                                  <span className="hidden sm:inline">{t('tyreOverview.discardAction')}</span>
                                </button>
                              ) : tyre.totalLapsDriven === 0 ? (
                                <span className="text-xs text-text-muted">{t('tyreOverview.summaryFresh')}</span>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
            </>
          )}
        </>
      )}

      {scanning && (
        <BarcodeScanner
          onScanSuccess={handleScanSuccess}
          onClose={() => setScanning(false)}
        />
      )}
    </div>
  );
}
